import Vue from 'vue';
import StateUtils from '@/utils/StateUtils.js';

// This store part is for wardrobe that is currently edited

const STEPS_ORDER = [
  'creatorType',
  'wardrobeDimensions',
  'wardrobePosition',
  'wardrobeDoorsNumber',
  'wardrobeDoorsDivision',
  'wardrobeDoorsMaterial',
  'wardrobeInventory'
];

export default {
  namespaced: true,
  state: {
    id: null,
    doors: {
      items: [], // items have following structure. {division_id:3, soft_close: false, fillings:[{filling_id:27},{filling_id:25}]}
      number: 0
    },
    doorsNumberObject: {
      door_width: 0,
      name: '',
      number: 0,
      price: 0
    },
    inventory: [],
    isApplySameMaterial: false,
    price: StateUtils.getDefaultWardrobePriceObject(),
    wardrobe: {
      gable_filling_id: null,
      gable_id: null,
      height: 0,
      impact: true,
      impact_filling_id: null,
      is_walk_in_closet: false,
      left_end_adjustment: false,
      left_stroke_adjustment: false,
      position_id: null,
      right_end_adjustment: false,
      right_stroke_adjustment: false,
      soft_close: false,
      width: 0
    },
    creatorMode: {
      hasDoor: true,
      hasInventory: true
    },
    stepsCompletion: {
      creatorType: false,
      wardrobeDimensions: false,
      wardrobePosition: false,
      wardrobeDoorsNumber: false,
      wardrobeDoorsDivision: false,
      wardrobeDoorsMaterial: false,
      wardrobeInventory: true
    },
    screenshots: {
      front: null,
      top: null,
      materials: null
    },
    isFromAutoSave: false
  },
  actions: {
    loadWardrobeForEditing ({ commit }, wardrobeData) {
      const data = JSON.parse(JSON.stringify(wardrobeData));

      commit('setWardrobeId', data.id);
      commit('setDoors', data.doors);
      commit('setDoorsNumberObject', data.doorsNumberObject);
      commit('setInventory', data.inventory);
      commit('setPrice', data.price);
      commit('setWardrobe', data.wardrobe);
      commit('setIsApplySameMaterial', data.isApplySameMaterial);

      commit('setCreatorMode', data.creatorMode);
      commit('setStepsCompletion', data.stepsCompletion);
      commit('setIsFromAutoSave', data.isFromAutoSave);

      if (data.screenshots) {
        commit('setScreenshots', data.screenshots);
      } else {
        commit('resetScreenShots');
      }
    },
    resetCreator ({ commit }, wardrobeID) {
      commit('setWardrobeId', wardrobeID);
      commit('resetDoors');
      commit('resetInventory');
      commit('resetPrice');
      commit('resetWardrobe');
      commit('resetStepsCompletion');
      commit('setCreatorMode', { hasDoor: true, hasInventory: true });
      commit('setIsApplySameMaterial', false);
      commit('resetScreenShots');
    }
  },
  mutations: {
    setWardrobeId (state, id) {
      Vue.set(state, 'id', id);
    },
    setDoors (state, doorsObject) {
      Object.assign(state.doors, doorsObject);
    },
    setDoorsNumber (state, doorsNumber) {
      Vue.set(state, 'doors', { ...StateUtils.getDoorsStateForNumber(state.doors, doorsNumber, state.hasSoftClose) });
    },
    setDoorsNumberObject (state, doorsNumberObject) {
      Object.assign(state.doorsNumberObject, doorsNumberObject);
    },
    setDoorsItems (state, doorsItems) {
      Object.assign(state.doors.items, doorsItems);
    },
    resetDoors (state) {
      Vue.set(state.doors, 'number', 0);
      Vue.set(state.doors, 'items', []);

      Vue.set(state.doorsNumberObject, 'door_width', 0);
      Vue.set(state.doorsNumberObject, 'name', '');
      Vue.set(state.doorsNumberObject, 'number', 0);
      Vue.set(state.doorsNumberObject, 'price', 0);
    },
    setWardrobe (state, wardrobeObject) {
      Object.assign(state.wardrobe, wardrobeObject);
    },
    setWardrobeHeight (state, height) {
      Vue.set(state.wardrobe, 'height', height);
    },
    setWardrobeWidth (state, width) {
      Vue.set(state.wardrobe, 'width', width);
    },
    setWardrobePositionId (state, positionId) {
      Vue.set(state.wardrobe, 'position_id', positionId);
    },
    setWardrobeGableId (state, gableId) {
      Vue.set(state.wardrobe, 'gable_id', gableId);

      if (!gableId) {
        Vue.set(state.wardrobe, 'gable_filling_id', null);
      }
    },
    setWardrobeGableFillingId (state, gableFillingId) {
      Vue.set(state.wardrobe, 'gable_filling_id', gableFillingId);
    },
    setPrice (state, price) {
      Vue.set(state, 'price', price);
    },
    setWardrobeUseImpact (state, useImpact) {
      Vue.set(state.wardrobe, 'impact', useImpact);

      if (!useImpact) {
        Vue.set(state.wardrobe, 'impact_filling_id', null);
      }
    },
    setWardrobeImpactFillingId (state, impactFillingId) {
      Vue.set(state.wardrobe, 'impact_filling_id', impactFillingId);
    },
    setWardrobeRightEndAdjustment (state, rightEndAdjustment) {
      Vue.set(state.wardrobe, 'right_end_adjustment', rightEndAdjustment);
    },
    setWardrobeRightStrokeAdjustment (state, rightStrokeAdjustment) {
      Vue.set(state.wardrobe, 'right_stroke_adjustment', rightStrokeAdjustment);
    },
    setWardrobeLeftEndAdjustment (state, leftEndAdjustment) {
      Vue.set(state.wardrobe, 'left_end_adjustment', leftEndAdjustment);
    },
    setWardrobeLeftStrokeAdjustment (state, leftStrokeAdjustment) {
      Vue.set(state.wardrobe, 'left_stroke_adjustment', leftStrokeAdjustment);
    },
    setWardrobeSoftClose (state, hasSoftClose) {
      Vue.set(state.wardrobe, 'soft_close', hasSoftClose);

      for (let i = 0; i < state.doors.items.length; i++) {
        state.doors.items[i].soft_close = hasSoftClose;
      }
    },
    setInventory (state, inventory) {
      Vue.set(state, 'inventory', inventory);
    },
    addInventoryItem (state, itemToAdd) {
      state.inventory.push(itemToAdd);
    },
    updateInventoryItemProp (state, { stageID, propName, newPropValue }) {
      let itemToUpdate = state.inventory.find(item => item.stageID === stageID);

      if (!itemToUpdate) {
        console.log('item not found ', stageID);
        return;
      }

      itemToUpdate[propName] = newPropValue;
    },
    addInventoryAttachedItem (state, { stageID, childID }) {
      let itemToUpdate = state.inventory.find(item => item.stageID === stageID);

      if (!itemToUpdate) {
        console.log('item not found ', stageID);
        return;
      }

      if (itemToUpdate.attachedItems.indexOf(childID) > -1) {
        return;
      }

      itemToUpdate.attachedItems.push(childID);
    },
    removeInventoryAttachedItem (state, { stageID, childID }) {
      let itemToUpdate = state.inventory.find(item => item.stageID === stageID);

      if (!itemToUpdate) {
        console.log('item not found ', stageID);
        return;
      }

      let childItemIndex = itemToUpdate.attachedItems.indexOf(childID);
      itemToUpdate.attachedItems.splice(childItemIndex, 1);
    },
    removeInventoryItem (state, stageID) {
      let itemToRemoveIndex = state.inventory.findIndex(item => item.stageID === stageID);
      if (itemToRemoveIndex > -1) {
        state.inventory.splice(itemToRemoveIndex, 1);

        let parent = state.inventory.find(item => item.attachedItems && item.attachedItems.indexOf(stageID) > -1);

        if (parent) {
          let itemIndex = parent.attachedItems.indexOf(stageID)
          parent.attachedItems.splice(itemIndex, 1);
        }
      }
    },
    resetInventory (state) {
      state.inventory = [];
    },
    resetPrice (state) {
      Object.assign(state.price, StateUtils.getDefaultWardrobePriceObject());
    },
    resetWardrobe (state) {
      Vue.set(state.wardrobe, 'gable_filling_id', null);
      Vue.set(state.wardrobe, 'gable_id', null);
      Vue.set(state.wardrobe, 'height', 0);
      Vue.set(state.wardrobe, 'impact', true);
      Vue.set(state.wardrobe, 'impact_filling_id', null);
      Vue.set(state.wardrobe, 'is_walk_in_closet', false);
      Vue.set(state.wardrobe, 'left_end_adjustment', false);
      Vue.set(state.wardrobe, 'left_stroke_adjustment', false);
      Vue.set(state.wardrobe, 'position_id', null);
      Vue.set(state.wardrobe, 'right_end_adjustment', false);
      Vue.set(state.wardrobe, 'right_stroke_adjustment', false);
      Vue.set(state.wardrobe, 'soft_close', false);
      Vue.set(state.wardrobe, 'width', 0);
    },
    setIsApplySameMaterial (state, isApplySameMaterial) {
      Vue.set(state, 'isApplySameMaterial', isApplySameMaterial);
    },
    setWardrobeIsWalkInCloset (state, isWalkInCloset) {
      Vue.set(state.wardrobe, 'is_walk_in_closet', isWalkInCloset);
    },
    setCreatorMode (state, creatorModeObject) {
      Object.assign(state.creatorMode, JSON.parse(JSON.stringify(creatorModeObject)));
      Vue.set(state.wardrobe, 'is_walk_in_closet', !creatorModeObject.hasDoor);
    },
    setStepsCompletion (state, stepsObject) {
      Object.assign(state.stepsCompletion, stepsObject);
    },
    setStepCompletion (state, step) {
      Vue.set(state.stepsCompletion, step.stepName, step.stepCompleted);
    },
    resetStepsCompletion (state) {
      Vue.set(state.stepsCompletion, 'creatorType', false);
      Vue.set(state.stepsCompletion, 'wardrobeDimensions', false);
      Vue.set(state.stepsCompletion, 'wardrobePosition', false);
      Vue.set(state.stepsCompletion, 'wardrobeDoorsNumber', false);
      Vue.set(state.stepsCompletion, 'wardrobeDoorsDivision', false);
      Vue.set(state.stepsCompletion, 'wardrobeDoorsMaterial', false);
      Vue.set(state.stepsCompletion, 'wardrobeInventory', false);
    },
    resetStepsAfter (state, currentStepName) {
      const currentIndex = STEPS_ORDER.indexOf(currentStepName);
      if (currentIndex === -1) {
        return;
      }

      for (let i = currentIndex + 1; i < STEPS_ORDER.length; i++) {
        const stepName = STEPS_ORDER[i];
        Vue.set(state.stepsCompletion, stepName, false);
      }
    },
    resetSpecificSteps(state, stepsArrayToReset) {
      if (!Array.isArray(stepsArrayToReset)) {
        return;
      }

      stepsArrayToReset.forEach(stepName => {
        if (Object.prototype.hasOwnProperty.call(state.stepsCompletion, stepName)) {
          Vue.set(state.stepsCompletion, stepName, false);
        }
      });
    },
    setScreenshots(state, value) {
      state.screenshots = {
        ...state.screenshots,
        ...value
      };
    },
    resetScreenShots (state) {
      state.screenshots = { front: null, top: null, materials: null };
    },
    setIsFromAutoSave (state, value) {
      Vue.set(state, 'isFromAutoSave', value);
    }
  },
  getters: {
    wardrobeId: (state) => state.id,
    doors: (state) => state.doors,
    doorsNumber: (state) => state.doors.number,
    doorsNumberObject: (state) => state.doorsNumberObject,
    doorsItems: (state) => state.doors.items,
    inventory: (state) => state.inventory,
    inventoryIDs: (state) => StateUtils.getInventoryIDs(state.inventory),
    wardrobe: (state) => state.wardrobe,
    wardrobeHeight: (state) => state.wardrobe.height,
    wardrobeWidth: (state) => state.wardrobe.width,
    wardrobeGableId: (state) => state.wardrobe.gable_id ? state.wardrobe.gable_id : null,
    wardrobeGableFillingId: (state) => state.wardrobe.gable_filling_id,
    wardrobeImpact: (state) => state.wardrobe.impact,
    wardrobeImpactFillingId: (state) => state.wardrobe.impact_filling_id,
    wardrobePositionId: (state) => state.wardrobe.position_id,
    wardrobeSoftClose: (state) => state.wardrobe.soft_close,
    price: (state) => state.price,
    wardrobeRightEndAdjustment: (state) => state.wardrobe.right_end_adjustment,
    wardrobeLeftEndAdjustment: (state) => state.wardrobe.left_end_adjustment,
    wardrobeRightStrokeAdjustment: (state) => state.wardrobe.right_stroke_adjustment,
    wardrobeLeftStrokeAdjustment: (state) => state.wardrobe.left_stroke_adjustment,
    isApplySameMaterial: (state) => state.isApplySameMaterial,
    stepsCompletionState: (state) => state.stepsCompletion,
    getIsStepCompleted: (state) => (stepName) => !!state.stepsCompletion[stepName],
    creatorMode: (state) => state.creatorMode,
    hasDoorOnly: (state) => state.creatorMode.hasDoor && !state.creatorMode.hasInventory,
    hasInventoryOnly: (state) => !state.creatorMode.hasDoor && state.creatorMode.hasInventory,
    hasFullWardrobe: (state) => state.creatorMode.hasDoor && state.creatorMode.hasInventory,
    hasDoor: (state) => state.creatorMode.hasDoor,
    hasInventory: (state) => state.creatorMode.hasInventory,
    creatorWardrobeData: (state) => ({
      id: state.id,
      wardrobe: state.wardrobe,
      doors: state.doors,
      doorsNumberObject: state.doorsNumberObject,
      inventory: state.inventory,
      price: state.price,
      creatorMode: state.creatorMode
    }),
    getScreenshots: (state, { screenShotView }) => state.screenshots[screenShotView],
    getIsWardrobeCreatorModeDisabled: (state) => StateUtils.getIsWardrobeCreatorModeDisabled(state.creatorMode, state.stepsCompletion, state.isFromAutoSave)
  }
};
